import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiColumns, FiCheck } from 'react-icons/fi';
import * as Styled from './ColumnPicker.styles';

/**
 * Compact "Columns" control for the new-UI tables — an icon button that opens a checklist of the
 * toggleable columns (the new-design equivalent of the legacy column picker). Each row shows a
 * checkbox so the visible columns read at a glance. Closes on outside-click / Escape.
 */
const ColumnPicker = ({ items, onToggle }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <Styled.Root ref={ref}>
      <Styled.Trigger
        type="button"
        active={open}
        onClick={() => setOpen((o) => !o)}
        title="Columns"
        aria-label="Toggle columns"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <FiColumns />
      </Styled.Trigger>

      {open && (
        <Styled.Menu role="group" aria-label="Toggle columns">
          <Styled.MenuLabel>Show columns</Styled.MenuLabel>
          {items.map((item) => (
            <Styled.Item
              key={item.key}
              type="button"
              role="menuitemcheckbox"
              aria-checked={item.visible}
              checked={item.visible}
              disabled={item.disabled}
              onClick={() => onToggle(item.key)}
            >
              <Styled.Box checked={item.visible}>{item.visible && <FiCheck />}</Styled.Box>
              {item.label}
            </Styled.Item>
          ))}
        </Styled.Menu>
      )}
    </Styled.Root>
  );
};

ColumnPicker.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      visible: PropTypes.bool,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default ColumnPicker;
